import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import CRMBodyComponent from 'src/components/crm/crm-body.component';
import HttpServices from 'src/services/httpService';
import { crmModuleURL } from 'src/services/urlService';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { CommonHelper } from 'src/utils/commonHelper';

// Extend dayjs with the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom styled MenuItem for profile select
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontWeight: 450,
  fontSize: '1.1rem',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

// Custom styled TextField for date range input
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '40px',
    fontSize: '0.875rem',
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiInputBase-input': {
    padding: '8px 14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

const CRM = ({handleNavCRMAccounts}) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const currentBrandTime = CommonHelper.getBrandTimeZoneTime();
  // State Variables
  const [connectedMedias, setConnectedMedias] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [dateRangeInput, setDateRangeInput] = useState('');
  const [dateRange, setDateRange] = useState([
    currentBrandTime.subtract(7, 'day').toDate(),
    currentBrandTime.toDate(),
  ]);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      setDateRangeInput(`${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`);
    }
  }, [dateRange]);

  useEffect(() => {
    getConnectedNetworks();
  }, []);

  const getConnectedNetworks = () => {
    let getProps = {
      url: crmModuleURL.getCRMAccounts,
      successCallback: connectedNetworksSuccessCallback,
      failureCallback: connectedNetworkFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const connectedNetworksSuccessCallback = (data, message) => {
    // Map API response to connectedMedias format
    const mappedProfiles = data.map((profile) => ({
      id: profile.connected_profile_details.connected_profile_id, 
      profile_name: profile.connected_profile_details.connected_profile_name,
      profile_image: profile.connected_profile_details.connected_profile_image,
      service_name : profile.connected_profile_details.social_media,
      is_enabled: profile.is_enabled,
    }));

    setConnectedMedias(mappedProfiles);
    if (mappedProfiles.length > 0) {
      // Select the first enabled profile (Instagram or Facebook Page)
      const enabledProfile = mappedProfiles.find(
        (profile) => profile.is_enabled &&  profile.profile_type !== "DIRECT PROFILE" && 
        (profile.service_name === 'instagram' || profile.service_name === 'facebook')
      );
      setSelectedProfile(enabledProfile);
    }
  };

  const connectedNetworkFailureCallback = (message) => {
    setConnectedMedias([]);
    setSelectedProfile(null);
  };

  const handleProfileChange = (event) => {
    const profileId = event.target.value;
    const profile = connectedMedias.find((p) => p.id === profileId);
    setSelectedProfile(profile);
  };

  const handleDateChange = (update) => {
    setDateRange(update);
    if (update[0] && update[1]) {
      setOpenDatePicker(false);
    }
  };

  const formatDate = (date) => {
    return dayjs(date).format('MMM D, YYYY');
  };

  return (
    <>
      <Helmet>
        <title>{translate('crm.title')}</title>
      </Helmet>
      {connectedMedias.length === 0 ? (
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 170px)',
            maxHeight: 'calc(100vh - 170px)',
            textAlign: 'center',
            p: 4,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              {translate('crm.no_webhook_accounts')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {translate('crm.connect_fb_ig_to_view_crm')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNavCRMAccounts}
            >
              {translate('crm.connect_account')}
            </Button>
          </CardContent>
        </Card>
      ) : (
<>
      <Box
        display="flex"
        alignItems="center"
        sx={{ mb: 2, gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}
      >
        <Select
          size="small"
          value={selectedProfile?.id || ''}
          onChange={handleProfileChange}
          displayEmpty
          sx={{
            minWidth: isMobile ? 250 : 450,
            '& .MuiSelect-select': {
              fontSize: '0.875rem',
              padding: '8px 32px 8px 12px',
              height: '25px',
              display: 'flex',
              alignItems: 'center',
            },
            backgroundColor: theme.palette.background.paper,
            '& .MuiOutlinedInput-root': {
              height: '40px',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem',
                  minHeight: '36px',
                },
              },
            },
          }}
          renderValue={(selected) =>
            selected ? (
              connectedMedias.find((profile) => profile.id === selected) ? (
                <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem' }}>
                    {CommonFunction.InitialAccountBadgeWithName(
                      connectedMedias.find((profile) => profile.id === selected),
                      24
                    )}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.875rem' }}>{selected}</Typography>
              )
            ) : (
              <Typography
                color="text.secondary"
                size="small"
                sx={{ fontSize: '0.875rem' }}
              >
                {translate('crm.select_source_profile')}
              </Typography>
            )
          }
        >
          {connectedMedias
            .filter(
              (profile) =>
                profile.service_name === 'instagram' || profile.service_name === 'facebook'
            )
            .map((profile) => (
              <StyledMenuItem
                key={profile.id}
                value={profile.id}
                sx={{
                  pointerEvents: !profile.is_enabled ? 'none' : 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minWidth: isMobile ? 250 : 450,
                  fontSize: '0.875rem',
                  minHeight: '36px',
                }}
              >
               <div style={{ opacity: !profile.is_enabled ? 0.5 : 1, flex: 1 }}>
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {CommonFunction.InitialAccountBadgeWithName(profile, 24)}
                </Typography>
              </div>
              {!profile.is_enabled && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavCRMAccounts()
                  }}
                  sx={{
                    ml: 2,
                    pointerEvents: 'auto',
                    borderRadius: 1,
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.error.dark,
                      boxShadow: theme.shadows[3],
                    },
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                    minWidth: 'auto',
                  }}
                >
                  {translate('crm.enable')}
                </Button>
              )}
              </StyledMenuItem>
            ))}
        </Select>
        <StyledTextField
          size="small"
          label={translate('crm.date_range')}
          value={dateRangeInput}
          onClick={() => setOpenDatePicker(true)}
          inputRef={anchorRef}
          sx={{
            minWidth: isMobile ? 200 : 250,
            '& .MuiInputBase-root': {
              height: '40px',
            },
          }}
          InputProps={{
            readOnly: true,
          }}
        />
        {openDatePicker && (
          <Popover
            open={openDatePicker}
            anchorEl={anchorRef.current}
            onClose={() => setOpenDatePicker(false)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box sx={{ p: 2 }}>
              <DatePicker
                selectsRange
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={handleDateChange}
                monthsShown={isMobile ? 1 : 2}
                inline
              />
            </Box>
          </Popover>
        )}
      </Box>

      {!selectedProfile ? (
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 200px)',
            maxHeight: 'calc(100vh - 200px)',
            textAlign: 'center',
            p: 4,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              {translate('crm.no_profile_selected')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {translate('crm.select_profile_to_view_crm')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {!openDatePicker ? (
            <CRMBodyComponent
              connectedProfileId={selectedProfile.id}
              serviceName={selectedProfile.service_name}
              startDate={dateRange[0].toISOString()}
              endDate={dateRange[1].toISOString()}
            />
          ) : (
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 200px)',
                maxHeight: 'calc(100vh - 200px)',
                textAlign: 'center',
                p: 4,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  {translate('crm.no_time_range_selected')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}
      </>)}
    </>
  );
};

export default CRM;